"use client"

import { createContext, useContext, useEffect, useMemo, useRef } from "react"
import { usePathname } from "next/navigation"
import {
  beginIdentityResolution,
  buildItemFromLineItem,
  buildItemFromProduct,
  buildUserData,
  completeIdentityResolution,
  currency,
  generateEventId,
  identityReady,
  normalizePageLocationForTracking,
  pushEvent,
  setTrackingSettings,
  sumQuantity,
  type AddressLike,
  type GtmItem,
  type KnownTrackingIdentity,
  type TrackingSettings,
} from "./gtm"
import { applyClarityConsent, applyClarityCountry } from "./clarity-client"

const TrackingContext = createContext<TrackingSettings | null>(null)

/**
 * V2.1-2 — settings are initialized SYNCHRONOUSLY during render (a guarded
 * one-time call in the component body, not in useEffect), so any child tracking
 * effect that fires on first commit already resolves the configured names/flags.
 *
 * Identity (2026-07-02): the logged-in customer is fetched CLIENT-side from
 * /api/identity (same-origin, ad-blocker-safe) instead of being server-rendered
 * — the old root-layout `retrieveCustomer()` forced every public page dynamic
 * (`no-store`). The gate is armed synchronously during the first render, BEFORE
 * any child tracking effect can fire, so `view_item` forwarding always waits
 * for the fetch to settle (see gtm.ts identityReady).
 *
 * M5 (2026-07-09): the gate is RE-armed and identity RE-fetched on every pathname
 * change. Login is a server action + soft redirect (lib/data/customer.ts), so the
 * root TrackingProvider never remounts and a mid-session login would otherwise
 * keep the shopper "guest" for every later event. Re-arming in the render body
 * (never an effect — child effects run before parent effects, so an effect arm
 * would let the new page's view_item race the fetch) keeps the gate contract:
 * view_item still awaits identityReady with no timeout racing it, and a failed
 * fetch still settles as guest. A same-path login (e.g. /account, whose @login
 * and @dashboard slots swap without a URL change) refreshes on the shopper's next
 * navigation — no tracked shopping event fires on /account itself.
 */
export function TrackingProvider({
  settings,
  children,
}: {
  settings: TrackingSettings
  children: React.ReactNode
}) {
  const initialized = useRef(false)
  if (!initialized.current) {
    setTrackingSettings(settings)
    initialized.current = true
  }

  const pathname = usePathname()
  const armedPath = useRef<string | null>(null)
  if (typeof window !== "undefined" && armedPath.current !== pathname) {
    beginIdentityResolution()
    armedPath.current = pathname
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let identity: KnownTrackingIdentity = null
      try {
        const res = await fetch("/api/identity", {
          cache: "no-store",
          // Guard against a pathological hang only — a normal failure/abort
          // settles the gate as guest. This is NOT a race that sends a
          // logged-in view_item identity-less: the gate stays closed until
          // the fetch itself settles.
          signal: AbortSignal.timeout(10_000),
        })
        if (res.ok) {
          const data = (await res.json()) as {
            identity?: KnownTrackingIdentity
            country?: string | null
          }
          identity = data?.identity ?? null
          // Clarity only. Deliberately OUTSIDE the identity gate contract below:
          // these are fire-and-forget tags, and nothing may delay or reorder
          // completeIdentityResolution (see rule 33a — view_item awaits it).
          applyClarityCountry(data?.country)
          applyClarityConsent()
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `[tracking] /api/identity responded ${res.status}; continuing as guest`
          )
        }
      } catch {
        // eslint-disable-next-line no-console
        console.warn(
          "[tracking] identity fetch failed; view_item proceeds without login identity"
        )
      }
      // A newer navigation already re-armed the gate and will settle it with its
      // own fetch; a stale result must not settle that gate.
      if (!cancelled) completeIdentityResolution(identity)
    })()
    return () => {
      cancelled = true
    }
  }, [pathname])

  return (
    <TrackingContext.Provider value={settings}>
      {children}
    </TrackingContext.Provider>
  )
}

type ProductArg = Parameters<typeof buildItemFromProduct>[0]
type VariantArg = Parameters<typeof buildItemFromProduct>[1]
type LineItemArg = Parameters<typeof buildItemFromLineItem>[0]

/**
 * Bound, memoized tracking helpers. Reads settings from context (always present
 * before children render). pushEvent itself re-checks the module mirror, so the
 * helpers stay thin.
 */
export function useTracking() {
  const settings = useContext(TrackingContext)

  return useMemo(() => {
    const cur = () => currency()

    return {
      settings,

      trackPageView(path?: string, pageType = "page") {
        const hasWin = typeof window !== "undefined"
        pushEvent("page_view", {
          event_id: generateEventId("page_view"),
          page_type: pageType,
          ...(path ? { page_path: path } : {}),
          // Top-level fields the GTM web container reads for page_view / Meta
          // PageView (actual_url, page_referrer, page_title DLV variables).
          ...(hasWin
            ? {
                page_title: document.title,
                actual_url: normalizePageLocationForTracking(
                  window.location.href
                ),
                page_referrer: document.referrer,
              }
            : {}),
        })
      },

      trackViewItem(product: ProductArg, variant: VariantArg) {
        const item = buildItemFromProduct(product, variant, 1)
        pushEvent("view_item", {
          event_id: generateEventId("view_item", item.item_id),
          page_type: "product",
          ecommerce: {
            currency: cur(),
            value: item.price ?? 0,
            items: [item],
          },
        })
      },

      trackViewItemList(
        listId: string,
        listName: string,
        items: GtmItem[]
      ) {
        pushEvent("view_item_list", {
          event_id: generateEventId("view_item_list", listId),
          page_type: "category",
          content_category: listName,
          ecommerce: {
            currency: cur(),
            item_list_id: listId,
            item_list_name: listName,
            items,
          },
        })
      },

      trackSelectItem(listId: string, listName: string, item: GtmItem) {
        pushEvent("select_item", {
          event_id: generateEventId("select_item", item.item_id),
          ecommerce: {
            item_list_id: listId,
            item_list_name: listName,
            items: [item],
          },
        })
      },

      trackAddToCart(product: ProductArg, variant: VariantArg, quantity = 1) {
        const item = buildItemFromProduct(product, variant, quantity)
        pushEvent("add_to_cart", {
          event_id: generateEventId("add_to_cart", item.item_id),
          ecommerce: {
            currency: cur(),
            value: (item.price ?? 0) * quantity,
            items: [item],
            total_item_quantity: quantity,
          },
        })
      },

      trackRemoveFromCart(item: GtmItem) {
        pushEvent("remove_from_cart", {
          event_id: generateEventId("remove_from_cart", item.item_id),
          ecommerce: {
            currency: cur(),
            value: (item.price ?? 0) * (item.quantity ?? 1),
            items: [item],
            total_item_quantity: item.quantity ?? 1,
          },
        })
      },

      trackViewCart(cartId: string, value: number, items: GtmItem[]) {
        pushEvent("view_cart", {
          event_id: generateEventId("view_cart", cartId),
          page_type: "cart",
          ecommerce: {
            currency: cur(),
            value,
            items,
            total_item_quantity: sumQuantity(items),
          },
        })
      },

      trackBeginCheckout(
        cartId: string,
        value: number,
        items: GtmItem[],
        opts?: { coupon?: string; userData?: Record<string, unknown> }
      ) {
        pushEvent("begin_checkout", {
          event_id: generateEventId("begin_checkout", cartId),
          page_type: "checkout",
          ecommerce: {
            currency: cur(),
            value,
            ...(opts?.coupon ? { coupon: opts.coupon } : {}),
            items,
            total_item_quantity: sumQuantity(items),
          },
          ...(opts?.userData ? { user_data: opts.userData } : {}),
        })
      },

      trackAddShippingInfo(
        cartId: string,
        value: number,
        items: GtmItem[],
        shippingTier?: string
      ) {
        pushEvent("add_shipping_info", {
          event_id: generateEventId("add_shipping_info", cartId),
          ecommerce: {
            currency: cur(),
            value,
            ...(shippingTier ? { shipping_tier: shippingTier } : {}),
            items,
          },
        })
      },

      trackAddPaymentInfo(
        cartId: string,
        value: number,
        items: GtmItem[],
        paymentType?: string
      ) {
        pushEvent("add_payment_info", {
          event_id: generateEventId("add_payment_info", cartId),
          ecommerce: {
            currency: cur(),
            value,
            ...(paymentType ? { payment_type: paymentType } : {}),
            items,
          },
        })
      },

      trackSearch(term: string) {
        const q = term.trim()
        if (!q) return
        pushEvent("search", {
          event_id: generateEventId("search"),
          page_type: "search",
          search_term: q,
        })
      },

      /** Resolves when the client identity fetch has settled (see gtm.ts). */
      identityReady,

      buildItemFromLineItem,
      buildItemFromProduct,
      buildUserData,
    }
  }, [settings])
}

export type { GtmItem, LineItemArg, AddressLike }
